import { useEffect, useId, useRef } from "react";
import clsx from "clsx";
import type { BookingLocale } from "@/i18n/types";
import { useBookingLanguage } from "@/lib/bookingLanguage";
import {
  getBookingLegalDocument,
  type BookingLegalContext,
  type BookingLegalDocument,
} from "@/lib/bookingLegalTexts";

type BookingLegalModalProps = {
  open: boolean;
  legalDocument: BookingLegalDocument;
  locale: BookingLocale;
  closeLabel: string;
  legalContext: BookingLegalContext;
  onClose: () => void;
};

export function BookingLegalModal({
  open,
  legalDocument,
  locale,
  closeLabel,
  legalContext,
  onClose,
}: BookingLegalModalProps) {
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const inquiry = useBookingLanguage() === "inquiry";
  const content = getBookingLegalDocument(locale, legalDocument, legalContext, { inquiry });

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={clsx(
          "relative z-[1] flex max-h-[min(88vh,calc(100dvh-1rem))] w-full max-w-2xl flex-col",
          "rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl",
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900 sm:text-xl">
            {content.title}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="bk-btn-secondary shrink-0 px-3 py-1.5 text-xs"
            onClick={onClose}
          >
            {closeLabel}
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          {content.intro ? (
            <p className="mb-5 text-sm leading-relaxed text-slate-600">{content.intro}</p>
          ) : null}
          <div className="space-y-5">
            {content.sections.map((section) => (
              <section key={section.title}>
                <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
                <div className="mt-2 space-y-2">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-relaxed text-slate-600">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
